<template>
	<div
		class="color-display"
		:style="colorStyle"
		:class="[`shape-${shape}`, `size-${size}`, { 'multi-color': isMultiColor, 'custom-color': showAsCustomColor }]"
		:title="colorTooltip"
	>
		<!-- Slot content (e.g., visibility toggle buttons) - always rendered when provided by parent -->
		<!-- Default content only shows when no slot content provided -->
		<slot>
			<span v-if="showAsCustomColor" class="custom-indicator">C</span>
			<span v-else>&nbsp;</span>
		</slot>
	</div>
</template>

<script>
export default {
	name: 'ColorDisplay',
	props: {
		hexCode: {
			type: String,
			default: ''
		},
		shape: {
			type: String,
			default: 'square', // 'square' or 'circle'
			validator: value => ['square', 'circle'].includes(value)
		},
		size: {
			type: String,
			default: 'medium', // 'small', 'medium', 'large', 'xl', 'auto'
			validator: value => ['small', 'medium', 'large', 'xl', 'auto'].includes(value)
		},
		stripeAngle: {
			type: Number,
			default: 90
		},
		label: {
			type: String,
			default: ''
		},
		isCustomColor: {
			type: Boolean,
			default: null // null means use label-based detection (legacy fallback)
		}
	},
	computed: {
		hexCodes() {
			if (!this.hexCode) return [];
			// Handle both comma-separated and single hex codes
			return this.hexCode.split(',')
				.map(h => h.trim())
				.filter(h => h && h.length > 0)
				.map(h => {
					// Ensure hex code doesn't have # prefix (it's added in styling)
					return h.startsWith('#') ? h.substring(1) : h;
				});
		},

		isMultiColor() {
			return this.hexCodes.length > 1;
		},

		showAsCustomColor() {
			// If isCustomColor prop is explicitly set (not null), use it
			if (this.isCustomColor !== null) {
				return this.isCustomColor;
			}

			// Legacy fallback: Check if it's a custom color based on label - show "C" indicator
			// Includes: Custom colors, handle types (not real colors), and numbered thread/shade labels
			if (this.label) {
				const upperLabel = this.label.toUpperCase().trim();

				// Standard custom color codes
				const customLabels = ['CUS', 'CUST', 'CUSTOM', 'CD', 'CH', 'BH'];
				if (customLabels.includes(upperLabel)) {
					return true;
				}

				// Numbered labels (thread/shade numbers) - with or without # prefix
				const numberedLabels = ['#8', '#11', '#15', '#16', '#20', '#21', '#22', '#23', '#24', '#25', '#26', '#27',
					'8', '11', '15', '16', '20', '21', '22', '23', '24', '25', '26', '27'];
				if (numberedLabels.includes(upperLabel)) {
					return true;
				}
			}
			return false;
		},

		colorTooltip() {
			if (!this.isMultiColor) return '';
			// Show hex codes in tooltip for multi-color
			return this.hexCodes.map(h => `#${h}`).join(', ');
		},

		colorStyle() {
			if (this.hexCodes.length === 0) {
				// No color specified - use light gray
				return {
					backgroundColor: '#CCCCCC'
				};
			}

			if (!this.isMultiColor) {
				// Single color - simple background
				return {
					backgroundColor: `#${this.hexCodes[0]}`
				};
			}

			// Multi-color - create striped gradient
			const colors = this.hexCodes;
			const stripeWidth = 100 / colors.length;

			// Build gradient stops for equal-width stripes
			const gradientStops = [];
			colors.forEach((color, index) => {
				const start = index * stripeWidth;
				const end = (index + 1) * stripeWidth;

				// Create hard stops for crisp stripes
				if (index > 0) {
					gradientStops.push(`#${colors[index - 1]} ${start}%`);
				}
				gradientStops.push(`#${color} ${start}%`);

				if (index === colors.length - 1) {
					gradientStops.push(`#${color} ${end}%`);
				}
			});

			// Create linear gradient for stripes
			return {
				background: `linear-gradient(${this.stripeAngle}deg, ${gradientStops.join(', ')})`,
				backgroundSize: '100% 100%'
			};
		}
	}
}
</script>

<style scoped lang="scss">
.color-display {
	display: inline-block;
	border: 1px solid #ddd;
	position: relative;
	box-sizing: border-box;

	// Add subtle shadow for better visibility
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

	// Custom color indicator
	.custom-indicator {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		font-weight: bold;
		color: #666;
		font-size: 0.8em;
		line-height: 1;
	}

	&.custom-color {
		border: 1px dashed #999;
		background-color: #FFFFFF;
	}

	&.multi-color {
		// Add visual indicator for multi-color items
		border-width: 1px;
		border-style: solid;
		border-color: #999;

		// Optional: Add subtle animation on hover
		transition: transform 0.2s ease;

		&:hover {
			transform: scale(1.1);
			box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
		}
	}

	// Size variations
	&.size-small {
		width: 20px;
		height: 20px;
		min-width: 20px;
		min-height: 20px;
	}

	&.size-medium {
		width: 30px;
		height: 30px;
		min-width: 30px;
		min-height: 30px;
	}

	&.size-large {
		width: 40px;
		height: 40px;
		min-width: 40px;
		min-height: 40px;
	}

	&.size-xl {
		width: 60px;
		height: 60px;
		min-width: 60px;
		min-height: 60px;
	}

	&.size-auto {
		width: 100%;
		height: 100%;
		min-height: 20px;
	}

	// Shape variations
	&.shape-circle {
		border-radius: 50%;
		overflow: hidden;
	}

	&.shape-square {
		border-radius: 3px;
	}
}

// Print media styles
@media print {
	.color-display {
		// Ensure colors print correctly
		-webkit-print-color-adjust: exact;
		print-color-adjust: exact;

		&.multi-color {
			// Ensure stripes are visible in print
			border: 1px solid #000 !important;
		}
	}
}
</style>